import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import { useGivenOrGeneratedId } from "../../hookHelpers";

import styles from "./Toggle.styles";

const { dotSchema, trackSchema } = styles;

const Dot = ({ className = "", size }) => (
  <div className={`${dotSchema[size]} ${className}`} data-role="button" />
);

Dot.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(["xsmall", "small", "large"]).isRequired,
};

const Track = ({ className = "", size }) => (
  <div className={`${trackSchema[size]} ${className}`} data-role="track" />
);

Track.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(["xsmall", "small", "large"]).isRequired,
};

const Toggle = ({
  id: idFromProps = null,
  checked,
  disabled = false,
  labelClassName = "",
  onChange = null,
  size = "small",
  dotClassName = "",
  showFlash = false,
  trackClassName = "",
  textClassName = "",
  text = "",
  labelPlacement = "end",
  ...otherProps
}) => {
  const id = useGivenOrGeneratedId("toggle", idFromProps);
  const [isDisabledFlashActive, setIsDisabledFlashActive] = useState(false);
  const disabledFlashTimeoutRef = useRef(null);
  const textContent = text ? <span className={textClassName}>{text}</span> : null;

  useEffect(
    () => () => {
      if (disabledFlashTimeoutRef.current) {
        clearTimeout(disabledFlashTimeoutRef.current);
      }
    },
    [],
  );

  const handleDisabledToggleClick = () => {
    if (!disabled || !showFlash) return;

    if (disabledFlashTimeoutRef.current) {
      clearTimeout(disabledFlashTimeoutRef.current);
    }

    setIsDisabledFlashActive(false);
    disabledFlashTimeoutRef.current = setTimeout(() => {
      setIsDisabledFlashActive(true);
      disabledFlashTimeoutRef.current = setTimeout(() => {
        setIsDisabledFlashActive(false);
        disabledFlashTimeoutRef.current = null;
      }, 200);
    }, 0);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <label
      htmlFor={id}
      onClick={handleDisabledToggleClick}
      className={`tw-flex tw-items-center tw-gap-[8px] tw-cursor-pointer tw-text-gray-75 ${size === "xsmall" ? "tw-text-12d" : "tw-text-14d"} ${labelClassName}`}
    >
      {labelPlacement === "start" && textContent}
      <div className="tw-relative" data-role="toggle">
        <input
          type="checkbox"
          id={id}
          className="tw-sr-only tw-peer"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          {...otherProps}
        />
        <Track
          size={size}
          className={`${trackClassName} ${isDisabledFlashActive ? "toggle-disabled-flash" : ""}`}
        />
        <Dot size={size} className={dotClassName} />
      </div>
      {labelPlacement === "end" && textContent}
    </label>
  );
};

Toggle.propTypes = {
  id: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  labelClassName: PropTypes.string,
  textClassName: PropTypes.string,
  onChange: PropTypes.func,
  size: PropTypes.oneOf(["xsmall", "small", "large"]),
  dotClassName: PropTypes.string,
  trackClassName: PropTypes.string,
  text: PropTypes.string,
  labelPlacement: PropTypes.oneOf(["start", "end"]),
  showFlash: PropTypes.bool,
};

export default Toggle;
